import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef as TanColumnDef,
  type ColumnSizingState,
} from '@tanstack/react-table';

import { cn } from '@/shared/lib/utils';
import { Checkbox } from '@/shared/ui/checkbox';
import { Icon } from '@/shared/ui/icon';

import type { ListColumnDef } from '../model/types';
import type { ListPageController } from '../model/useListPageController';
import { EmptyResult } from '../regions/ListStates';

const SELECT_COLUMN_ID = '__select';
const ACTIONS_COLUMN_ID = '__actions';

interface ColumnMeta<TItem> {
  def?: ListColumnDef<TItem>;
}

/**
 * Sloupcová tabulka (anatomie 03) — sticky hlavička, zmrazená pásma vlevo (výběr · akce ·
 * připnuté sloupce s hranou), výběr řádků, řazení hlavičkou, volitelná ruční šířka sloupců
 * a synchronizovaný horní vodorovný scrollbar. Headless engine: TanStack Table (řízený stav
 * sloupců — viditelnost, pořadí, pinning, šířky); data vlastní React Query přes controller.
 */
export function ListTable<TItem>({
  controller,
  columns,
  pickerMode,
}: {
  controller: ListPageController<TItem>;
  columns: ListColumnDef<TItem>[];
  /** Picker režim — klik na řádek přepíná výběr. */
  pickerMode?: boolean;
}) {
  const { t } = useTranslation('listPage');
  const defs = useMemo(() => new Map(columns.map((c) => [c.key, c])), [columns]);
  const manual = controller.widthMode === 'manual';

  const tanColumns = useMemo<TanColumnDef<TItem>[]>(() => {
    const dataColumns = controller.columnStates
      .map((state) => defs.get(state.key))
      .filter((def): def is ListColumnDef<TItem> => def !== undefined)
      .map(
        (def): TanColumnDef<TItem> => ({
          id: def.key,
          header: def.label,
          size: def.w,
          minSize: 48,
          cell: (info) => def.cell(info.row.original),
          meta: { def } satisfies ColumnMeta<TItem>,
        })
      );
    return [
      { id: SELECT_COLUMN_ID, size: 34, minSize: 34 },
      { id: ACTIONS_COLUMN_ID, size: 34, minSize: 34 },
      ...dataColumns,
    ];
  }, [controller.columnStates, defs]);

  const columnSizing = useMemo<ColumnSizingState>(() => {
    const sizing: ColumnSizingState = {};
    for (const state of controller.columnStates) {
      if (state.width !== undefined) {
        sizing[state.key] = state.width;
      }
    }
    return sizing;
  }, [controller.columnStates]);

  const table = useReactTable({
    data: controller.items,
    columns: tanColumns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => String(controller.rowKey(row)),
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    enableColumnResizing: manual,
    columnResizeMode: 'onChange',
    state: {
      columnOrder: [
        SELECT_COLUMN_ID,
        ACTIONS_COLUMN_ID,
        ...controller.columnStates.map((s) => s.key),
      ],
      columnVisibility: Object.fromEntries(controller.columnStates.map((s) => [s.key, s.visible])),
      columnPinning: {
        left: [
          SELECT_COLUMN_ID,
          ACTIONS_COLUMN_ID,
          ...controller.columnStates.filter((s) => s.pinned && s.visible).map((s) => s.key),
        ],
      },
      columnSizing,
    },
    onColumnSizingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(columnSizing) : updater;
      for (const [key, width] of Object.entries(next)) {
        if (columnSizing[key] !== width && defs.has(key)) {
          controller.setColumnWidth(key, width);
        }
      }
    },
  });

  const leftColumns = table.getLeftLeafColumns();
  const lastPinnedId = leftColumns[leftColumns.length - 1]?.id;

  // Horní synchronizovaný vodorovný scrollbar — posuv dosažitelný bez scrollování dolů (03 §1).
  const wrapRef = useRef<HTMLDivElement>(null);
  const topRef = useRef<HTMLDivElement>(null);
  const syncing = useRef(false);
  const [scrollWidth, setScrollWidth] = useState(0);
  const [overflowing, setOverflowing] = useState(false);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) {
      return;
    }
    const measure = () => {
      setScrollWidth(wrap.scrollWidth);
      setOverflowing(wrap.scrollWidth > wrap.clientWidth + 1);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(wrap);
    return () => observer.disconnect();
  }, [controller.columnStates, controller.items.length]);

  const syncScroll = (from: HTMLElement | null, to: HTMLElement | null) => {
    if (syncing.current) {
      syncing.current = false;
      return;
    }
    if (from && to) {
      syncing.current = true;
      to.scrollLeft = from.scrollLeft;
    }
  };

  const sticky = (columnId: string): React.CSSProperties | undefined => {
    const column = table.getColumn(columnId);
    if (!column || !column.getIsPinned()) {
      return undefined;
    }
    return { position: 'sticky', left: column.getStart('left'), zIndex: 2 };
  };

  return (
    <div className="phxlist-tablezone">
      {overflowing ? (
        <div
          ref={topRef}
          className="phxlist-hscroll-top"
          onScroll={() => syncScroll(topRef.current, wrapRef.current)}
        >
          <div style={{ width: scrollWidth, height: 1 }} />
        </div>
      ) : null}
      <div
        ref={wrapRef}
        className="phxlist-tablewrap"
        onScroll={() => syncScroll(wrapRef.current, topRef.current)}
      >
        <table
          className="phxlist-table"
          data-sizing={controller.widthMode}
          data-picker={pickerMode || undefined}
        >
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const columnId = header.column.id;
                  if (columnId === SELECT_COLUMN_ID) {
                    return (
                      <th
                        key={header.id}
                        className="phxlist-fz phxlist-fz-sel"
                        style={{ ...sticky(columnId), width: 34, minWidth: 34 }}
                      >
                        <Checkbox
                          checked={controller.pageAllSelected}
                          indeterminate={controller.pageSomeSelected && !controller.pageAllSelected}
                          onCheckedChange={controller.togglePageSelected}
                        />
                      </th>
                    );
                  }
                  if (columnId === ACTIONS_COLUMN_ID) {
                    return (
                      <th
                        key={header.id}
                        className={cn('phxlist-fz', columnId === lastPinnedId && 'phxlist-fz-edge')}
                        style={{ ...sticky(columnId), width: 34, minWidth: 34 }}
                      />
                    );
                  }
                  const def = (header.column.columnDef.meta as ColumnMeta<TItem>)?.def;
                  const width = header.getSize();
                  const sortable = def?.sortable !== false;
                  const sortKey = def?.sortKey ?? columnId;
                  const active = controller.sort.column === sortKey;
                  return (
                    <th
                      key={header.id}
                      className={cn(
                        header.column.getIsPinned() && 'phxlist-fz',
                        columnId === lastPinnedId && 'phxlist-fz-edge'
                      )}
                      style={{
                        ...sticky(columnId),
                        width,
                        minWidth: width,
                        textAlign: def?.align,
                      }}
                    >
                      <span className="phxlist-th">
                        <span
                          className={cn('phxlist-th-sort', !sortable && 'cursor-default')}
                          onClick={sortable ? () => controller.sortBy(sortKey) : undefined}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {active ? (
                            <Icon name={controller.sort.desc ? 'chevdown' : 'chevup'} size={11} />
                          ) : sortable ? (
                            <span className="phxlist-th-sorticon">
                              <Icon name="sort" size={11} />
                            </span>
                          ) : null}
                        </span>
                        <button
                          type="button"
                          className="phxlist-pinbtn phxlist-th-pin"
                          data-on={header.column.getIsPinned() || undefined}
                          title={
                            header.column.getIsPinned() ? t('columns.unpin') : t('columns.pin')
                          }
                          onClick={() => controller.togglePinColumn(columnId)}
                        >
                          <Icon name="pin" size={11} />
                        </button>
                      </span>
                      {manual && header.column.getCanResize() ? (
                        <span
                          className="phxlist-resizer"
                          title={t('columns.resize')}
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                        />
                      ) : null}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => {
              const key = controller.rowKey(row.original);
              const selected = controller.selection.has(key);
              return (
                <tr
                  key={row.id}
                  data-selected={selected || undefined}
                  onClick={pickerMode ? () => controller.toggleSelected(key) : undefined}
                >
                  {row.getVisibleCells().map((cell) => {
                    const columnId = cell.column.id;
                    if (columnId === SELECT_COLUMN_ID) {
                      return (
                        <td
                          key={cell.id}
                          className="phxlist-fz phxlist-fz-sel"
                          style={{ ...sticky(columnId), width: 34, minWidth: 34 }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Checkbox
                            checked={selected}
                            onCheckedChange={() => controller.toggleSelected(key)}
                          />
                        </td>
                      );
                    }
                    if (columnId === ACTIONS_COLUMN_ID) {
                      return (
                        <td
                          key={cell.id}
                          className={cn(
                            'phxlist-fz',
                            columnId === lastPinnedId && 'phxlist-fz-edge'
                          )}
                          style={{ ...sticky(columnId), width: 34, minWidth: 34 }}
                        >
                          <RowOpenAction row={row.original} controller={controller} />
                        </td>
                      );
                    }
                    const def = (cell.column.columnDef.meta as ColumnMeta<TItem>)?.def;
                    const width = cell.column.getSize();
                    return (
                      <td
                        key={cell.id}
                        className={cn(
                          cell.column.getIsPinned() && 'phxlist-fz',
                          columnId === lastPinnedId && 'phxlist-fz-edge'
                        )}
                        style={{
                          ...sticky(columnId),
                          width,
                          minWidth: width,
                          textAlign: def?.align,
                        }}
                      >
                        {def?.link && controller.canOpen ? (
                          <a
                            className="phxlist-rowlink"
                            onClick={() => controller.openRecord(row.original)}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </a>
                        ) : (
                          flexRender(cell.column.columnDef.cell, cell.getContext())
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
        {controller.items.length === 0 && !controller.isLoading ? <EmptyResult /> : null}
      </div>
    </div>
  );
}

function RowOpenAction<TItem>({
  row,
  controller,
}: {
  row: TItem;
  controller: ListPageController<TItem>;
}) {
  const { t } = useTranslation('listPage');
  if (!controller.canOpen) {
    return null;
  }
  return (
    <button
      type="button"
      className="phxlist-rowact"
      title={t('table.openDetail')}
      onClick={(e) => {
        e.stopPropagation();
        controller.openRecord(row);
      }}
    >
      <Icon name="edit" size={13} />
    </button>
  );
}
